import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useDatabase } from '../../context/DatabaseContext';

// Replaced FiBarChart3 with FiBarChart2 as FiBarChart3 is not standard in react-icons/fi
const { FiTrendingUp, FiActivity, FiBarChart2 } = FiIcons;

const TechnicalAnalytics = () => {
  const { products } = useDatabase();

  // Analyse der Tragzahlen-Verteilung
  const bearingLoadAnalysis = products
    .filter(p => p.specifications.dynamic_load_rating_n)
    .reduce((acc, product) => {
      const loadRating = product.specifications.dynamic_load_rating_n;
      const range = loadRating < 10000 ? 'Leicht (< 10kN)' : 
                   loadRating < 25000 ? 'Mittel (10-25kN)' : 'Schwer (> 25kN)';
      acc[range] = (acc[range] || 0) + 1;
      return acc;
    }, {});

  // Materialverteilung in der Dichtungstechnik
  const sealMaterialAnalysis = products
    .filter(p => p.etim_class_code === 'EC002306' && p.specifications.material)
    .reduce((acc, product) => {
      const material = product.specifications.material;
      acc[material] = (acc[material] || 0) + 1;
      return acc;
    }, {});

  const loadDistributionOption = {
    title: {
      text: 'Tragzahl-Verteilung Wälzlager',
      textStyle: { fontSize: 14, fontWeight: 'bold', color: '#1f2937' }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    color: ['#dc2626'],
    xAxis: {
      type: 'category',
      data: Object.keys(bearingLoadAnalysis),
      axisLabel: { rotate: 45, fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      name: 'Anzahl Produkte'
    },
    series: [{
      type: 'bar',
      data: Object.values(bearingLoadAnalysis),
      itemStyle: {
        borderRadius: [4, 4, 0, 0],
        gradient: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: '#dc2626' },
            { offset: 1, color: '#7f1d1d' }
          ]
        }
      }
    }]
  };

  const materialDistributionOption = {
    title: {
      text: 'Materialverteilung Dichtungen',
      textStyle: { fontSize: 14, fontWeight: 'bold', color: '#1f2937' }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)'
    },
    color: ['#000000', '#dc2626', '#7f1d1d', '#991b1b'],
    series: [{
      type: 'pie',
      radius: '70%',
      data: Object.entries(sealMaterialAnalysis).map(([material, count]) => ({
        name: material,
        value: count
      })),
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }]
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiActivity} className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Technische Analyse</h2>
            <p className="text-gray-600">Verteilung der Leistungsdaten</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-64">
          <ReactECharts 
            option={loadDistributionOption} 
            style={{ height: '100%', width: '100%' }}
          />
        </div>
        <div className="h-64">
          <ReactECharts 
            option={materialDistributionOption} 
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-red-50 p-4 rounded-lg border border-red-100">
          <div className="flex items-center space-x-2 mb-2">
            <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-red-600" />
            <h3 className="font-semibold text-red-900">Hochlast-Segment</h3>
          </div>
          <p className="text-sm text-red-800">
            {Math.round((Object.values(bearingLoadAnalysis)[2] || 0) / products.length * 100)}% 
            der Lager für Schwerlastanwendungen
          </p>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-2 mb-2">
            <SafeIcon icon={FiBarChart2} className="w-4 h-4 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Materialvielfalt</h3>
          </div>
          <p className="text-sm text-gray-600">
            {Object.keys(sealMaterialAnalysis).length} verschiedene 
            Dichtungswerkstoffe verfügbar
          </p>
        </div>

        <div className="bg-black text-white p-4 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <SafeIcon icon={FiActivity} className="w-4 h-4" />
            <h3 className="font-semibold">Präzisionssegment</h3>
          </div>
          <p className="text-sm text-gray-300">
            Lineartechnik mit Genauigkeitsklassen 
            von Normal bis Ultra-Precision
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default TechnicalAnalytics;